import { useMemo } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { getPracticeAnalyticsQueryVariables } from '../helpers/query-variables';
import { usePracticeAnalyticsShallowStore } from './use-practice-analytics-store';

const totals = `
  accepted
  acceptedPercent
  diagnosed
  diagnosedPercent
  qualifiedVisits
  unscheduledTreatment
`;

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      hygieneTreatmentPlan: treatmentPlan(treatmentPlanType: HYGIENE) {
        totals(start: $start, end: $end, step: $step) {
          ${totals}
        }
      }
      restorativeTreatmentPlan: treatmentPlan(treatmentPlanType: RESTORATIVE) {
        totals(start: $start, end: $end, step: $step) {
          ${totals}
        }
      }
    }
  }
`;
export const useGetPractitionerMetric = () => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');

  const { demoData, filters, isDemoAccount } = usePracticeAnalyticsShallowStore('demoData', 'filters', 'isDemoAccount');

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `practitioner-analysis-overview-${isDemoAccount}-${JSON.stringify(filters)}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.PractitionerAnalysisResponse>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.practitionerAnalysisSummary : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { aggregatedData, rawData } = useMemo(() => {
    return PracticeAnalyticsAggregations.pratitionerAnalysisSummary(data);
  }, [data]);

  return {
    aggregatedPractitionerAnalysisData: aggregatedData,
    isLoading,
    rawPractitionerAnalysisData: rawData,
  };
};
